import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Reveal } from "./Reveal";
import { DASHBOARD_CARDS } from "@/data/exceptionHub";
import { PriorityBadge, StatusPill, Progress, Avatar } from "./primitives";
import { fadeUp, stagger, viewport } from "@/lib/motion";

export const DashboardCards = () => {
  return (
    <section id="dashboard" className="relative py-24 md:py-32 bg-white" data-testid="section-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal><span className="text-xs font-semibold uppercase tracking-[0.15em] text-brand">Intelligent Exception Dashboard</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-navy tracking-tight leading-tight text-balance">
              Every category of exception, organized and owned.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-ink-500 leading-relaxed">
              A calm, executive view of everything that needs attention — with priority, owner, status and live progress on each card.
            </p>
          </Reveal>
        </div>

        <motion.div variants={stagger(0.08)} initial="hidden" whileInView="show" viewport={viewport}
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {DASHBOARD_CARDS.map((card) => {
            const Icon = Icons[card.icon] || Icons.Circle;
            const done = card.progress === 100;
            return (
              <motion.article key={card.title} variants={fadeUp} whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                data-testid={`dashboard-card-${card.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group rounded-2xl bg-white border border-slate-100 shadow-soft hover:shadow-card transition-shadow p-6 md:p-7">
                <div className="flex items-start justify-between">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-soft text-brand group-hover:bg-brand group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <PriorityBadge priority={card.priority} />
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-navy tracking-tight">{card.title}</h3>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Avatar src={card.avatar} alt={card.owner} size="h-6 w-6" />
                    <span className="text-xs text-ink-500">{card.owner}</span>
                  </div>
                  <span className="text-xs text-ink-300">{card.time}</span>
                </div>
                <div className="mt-5 flex items-center justify-between text-xs">
                  <StatusPill status={card.status} done={done} />
                  <span className="text-ink-400 tabular-nums">{card.progress}%</span>
                </div>
                <div className="mt-2.5"><Progress value={card.progress} done={done} /></div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
